import { Box, Grid, Stack } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import {
  candiateDefaultFieldMapping,
  IMappingFormSetting,
  IMappingFormSettingsResponse,
  saveCandiateFieldMapping,
} from 'wisestep-api';
import { Spinner, WsButton, WsTypography, WsTypoVariants } from 'wisestep-base';

import { SettingsPageLayout } from '../../../components/SettingsPageLayout';
import { useWorkspaceContext } from '../../../context';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../../features/teams/helper/defaultValues';
import { AtsCustomAPIAccess, useAtsCustomAPIAccess } from '../../../hooks/useAtsCustomAPIAccess';
import useMappingFormSettings from '../../../hooks/useMappingFormSettings';
import { warning, success, failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { HeaderBox } from '../../jobSettings/components/HeaderBox';

import GridRowItem from './gridRowItem';

const ConfigMappingFormPage = () => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const workspaceContext = useWorkspaceContext();
  const mappingFormSettingsResponse = useMappingFormSettings();
  const [fieldsMap, setFieldsMap] = useState<IMappingFormSettingsResponse>(
    mappingFormSettingsResponse?.fields ?? ({} as IMappingFormSettingsResponse)
  );

  const { access: atsCustomAccessControl } = useAtsCustomAPIAccess([
    AtsCustomAPIAccess.MANAGE_MAPPING_FIELDS_CUSTOMISATION,
  ]);

  const manageAccess = workspaceContext.owns || atsCustomAccessControl[0];

  useEffect(() => {
    if (mappingFormSettingsResponse.fields) setFieldsMap(mappingFormSettingsResponse.fields);
  }, [mappingFormSettingsResponse.fields]);

  useEffect(() => {
    (() => {
      if (mappingFormSettingsResponse.status === 'success')
        setNotification(success('Candidate Fields Mapping loaded Successfully'));
      if (mappingFormSettingsResponse.status === 'failed')
        setNotification(failed('Error while loading field settings'));
    })();
  }, [mappingFormSettingsResponse.status, setNotification]);

  const resetDefaultSettingsDetails = async () => {
    try {
      const resetDefaultSettingsDetails = await candiateDefaultFieldMapping();
      const defaultList = resetDefaultSettingsDetails?.data ?? {};
      setFieldsMap(() => {
        saveCandidateMappingField();
        return defaultList;
      });
      setNotification(success('Candidate Default Fields Mapping loaded Successfully'));
    } catch (e) {
      console.log(e);
    }
  };

  const saveCandidateMappingField = async () => {
    try {
      if (manageAccess) {
        await saveCandiateFieldMapping(fieldsMap);
        setNotification(success('Candidate Fields Mapping saved Successfully'));
      } else {
        setNotification(warning('You do not have enough permissions to perform this action'));
      }
    } catch (error) {
      setNotification(warning('Something Went Wrong , Please try again'));
    }
  };

  const onRowChange = (newRow: IMappingFormSetting, key: string) => {
    if (newRow.mandatory) {
      if (!newRow.show) newRow.show = true;
      newRow.hidden = true;
    }
    console.log(newRow);
    setFieldsMap((currentMap) => ({ ...currentMap, [key]: newRow }));
  };

  return (
    <SettingsPageLayout
      title="Mapping Form"
      indexOptions={[{ label: 'Mapping Form', url: '/crm-settings/configure-mapping-form' }]}
      breadCrumnbsList={getSettingBreadCrumbList(
        ISettingBreadCrumbTypes.CRM,
        Pages['MAPPING_FORM']
      )}
    >
      {!isEmpty(fieldsMap) ? (
        <Box px={8} pt={2}>
          <HeaderBox mb={4}>
            <WsTypography variant={WsTypoVariants.SUB_TITLE}>Mapping Fields</WsTypography>
            <Stack direction="row" spacing={1}>
              <WsButton type="submit" size="small" onClick={saveCandidateMappingField}>
                Save
              </WsButton>
              <WsButton size="small" onClick={resetDefaultSettingsDetails}>
                Reset To Default
              </WsButton>
            </Stack>
          </HeaderBox>
          <Grid container direction="column" rowSpacing={1} mb={2}>
            {Object.keys(fieldsMap)?.map((key) => (
              <GridRowItem
                key={key}
                fieldItem={fieldsMap[key]}
                fieldKey={key}
                onRowChange={onRowChange}
              />
            ))}
          </Grid>
        </Box>
      ) : (
        <Spinner />
      )}
    </SettingsPageLayout>
  );
};

export default ConfigMappingFormPage;
